import type { CreateResumeDto } from "@reactive-resume/dto";
import slugify from "@sindresorhus/slugify";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useToast } from "@/client/hooks/use-toast";
import { saveAtsTarget } from "@/client/services/ats/ats-target";
import { useCreateResume } from "@/client/services/resume/create";
import { useDialog } from "@/client/stores/dialog";

import { useFormResumeCreate } from "../../_components/form-create";
import { OnboardingShell } from "./onboarding/components/onboarding-shell";
import { CareerStep } from "./onboarding/steps/career-step";
import { ObjectiveStep } from "./onboarding/steps/objective-step";
import { StartMethodStep } from "./onboarding/steps/start-method-step";
import { SummaryStep } from "./onboarding/steps/summary-step";
import { TemplateSelectionStep } from "./onboarding/steps/template-selection-step";
import type { CreateResumeAnswers, ManualStep, StartMethod } from "./onboarding/types";

const INITIAL_ANSWERS: CreateResumeAnswers = {
  method: "scratch",
  profile: null,
  experience: null,
  targetJob: "",
  sector: "",
  applicationType: null,
  jobDescription: "",
  template: null,
};

const GUIDE = {
  1: {
    image: "/images/user-space/onboarding/create-resume/guide-start.webp",
    message: "Bienvenue ! Je vous accompagne.",
  },
  2: {
    image: "/images/user-space/onboarding/create-resume/guide-career.webp",
    message: "Parfait ! Parlez-moi de votre parcours.",
  },
  3: {
    image: "/images/user-space/onboarding/create-resume/guide-objective.webp",
    message: "Quel poste souhaitez-vous décrocher ?",
  },
  4: {
    image: "/images/user-space/onboarding/create-resume/guide-summary.webp",
    message: "Tout est prêt !",
  },
  5: {
    image: "/images/user-space/onboarding/create-resume/guide-template.webp",
    message: "Choisissez maintenant votre modèle idéal !",
  },
} satisfies Record<
  ManualStep,
  {
    image: string;
    message: string;
  }
>;

export const CreationFormResume = () => {
  const formContext = useFormResumeCreate();

  const documentDialog = useDialog("import-ocr");

  const linkedinDialog = useDialog("import-linkedin");

  const navigate = useNavigate();
  const { toast } = useToast();

  const { createResume, loading } = useCreateResume();

  const [step, setStep] = useState<ManualStep>(1);

  const [answers, setAnswers] = useState<CreateResumeAnswers>(INITIAL_ANSWERS);

  const reset = () => {
    setStep(1);
    setAnswers(INITIAL_ANSWERS);
  };

  const close = () => {
    if (loading) {
      return;
    }

    formContext?.setformCreateResume(false);

    reset();
  };

  const update = <Key extends keyof CreateResumeAnswers>(
    key: Key,
    value: CreateResumeAnswers[Key],
  ) => {
    setAnswers((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const switchToImport = (method: Exclude<StartMethod, "scratch">) => {
    if (method === "document") {
      documentDialog.open("create");
    } else {
      linkedinDialog.open("create");
    }

    window.requestAnimationFrame(() => {
      formContext?.setformCreateResume(false);

      reset();
    });
  };

  const continueFromStart = () => {
    if (answers.method === "scratch") {
      setStep(2);
      return;
    }

    switchToImport(answers.method);
  };

  const submit = async () => {
    if (!answers.template || loading) {
      return;
    }

    const title = `CV ${answers.targetJob.trim() || "professionnel"}`;

    const suffix = Date.now().toString().slice(-7);

    const slug = `${slugify(title) || "cv-professionnel"}-${suffix}`;

    try {
      const resume = await createResume({
        title,
        slug,
        visibility: "private",
        template: answers.template as CreateResumeDto["template"],
      });

      /*
       * La cible ATS est enregistrée
       * uniquement après la création,
       * lorsque l’identifiant définitif
       * du CV est disponible.
       */
      saveAtsTarget(resume.id, {
        targetJob: answers.targetJob,
        sector: answers.sector,
        applicationType: answers.applicationType,
        jobDescription: answers.jobDescription,
        source: "onboarding",
      });

      formContext?.setformCreateResume(false);

      reset();

      await navigate(`/client/builder/${resume.id}`);
    } catch (error) {
      const message =
        (
          error as {
            response?: {
              data?: {
                message?: string;
              };
            };
            message?: string;
          }
        )?.response?.data?.message ??
        (error as Error).message ??
        "La création du CV a échoué.";

      toast({
        variant: "destructive",
        title: "Création impossible",
        description: message,
      });
    }
  };

  useEffect(() => {
    if (!formContext?.formCreateResume) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !loading) {
        close();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [formContext?.formCreateResume, loading]);

  if (!formContext?.formCreateResume) {
    return null;
  }

  const guide = GUIDE[step];

  return (
    <OnboardingShell
      step={step}
      illustration={guide.image}
      message={guide.message}
      busy={loading}
      onClose={close}
      onBack={
        step === 1
          ? close
          : () => {
              setStep((step - 1) as ManualStep);
            }
      }
    >
      {step === 1 && (
        <StartMethodStep
          value={answers.method}
          onChange={(value) => {
            update("method", value);
          }}
          onContinue={continueFromStart}
        />
      )}

      {step === 2 && (
        <CareerStep
          profile={answers.profile}
          experience={answers.experience}
          onProfileChange={(value) => {
            update("profile", value);
          }}
          onExperienceChange={(value) => {
            update("experience", value);
          }}
          onContinue={() => {
            setStep(3);
          }}
        />
      )}

      {step === 3 && (
        <ObjectiveStep
          targetJob={answers.targetJob}
          sector={answers.sector}
          applicationType={answers.applicationType}
          jobDescription={answers.jobDescription}
          onTargetJobChange={(value) => {
            update("targetJob", value);
          }}
          onSectorChange={(value) => {
            update("sector", value);
          }}
          onApplicationTypeChange={(value) => {
            update("applicationType", value);
          }}
          onJobDescriptionChange={(value) => {
            update("jobDescription", value);
          }}
          onContinue={() => {
            setStep(4);
          }}
        />
      )}

      {step === 4 && (
        <SummaryStep
          answers={answers}
          onEdit={setStep}
          onContinue={() => {
            setStep(5);
          }}
        />
      )}

      {step === 5 && (
        <TemplateSelectionStep
          selected={answers.template}
          busy={loading}
          onSelect={(value) => {
            update("template", value);
          }}
          onCreate={() => {
            void submit();
          }}
        />
      )}
    </OnboardingShell>
  );
};
